import React, { useEffect, useState, useRef, useCallback } from 'react';
import Api from "../../../services/api"; 
import moment from 'moment'; 
import { 
    SimpleGrid, 
    Box, 
    Heading, 
    VStack, 
    Flex, 
    Text, 
    Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { 
    AiOutlineTeam, 
    AiOutlineCheckCircle, 
    AiOutlineWarning, 
    AiOutlineCloseCircle,
    AiOutlineSchedule, 
    AiOutlineCalendar, 
    AiOutlineClockCircle,
    AiOutlineDollar,
} from 'react-icons/ai'; 
import { 
    BsPeople, 
    BsCheckCircle, 
    BsExclamationTriangle 
} from 'react-icons/bs'; 

import StatusCard from '../../../components/UI/StatusCard'; 
import InsightCard from '../../../components/UI/InsightCard';
import CrossSellTable from '../../../components/charts/CrossSellTable'; 
import ProductDistributionChart from '../../../components/charts/ProductDistributionChart';
import ClientStatusChart from '../../../components/charts/ClientStatusChart'; 
import InadimplenciaChart from '../../../components/charts/InadimplenciaChart';
import InadimplenciaPorTagChart from '../../../components/charts/InadimplenciaPorTagChart';
import { Loading } from '../../../components/loading'; 
import DashboardHeader from '../../../components/Layout/DashboardHeader'; 

const STATUS_COLORS = {
    'Em Dia': '#48BB78', 
    'Inadimplente': '#bbaf06ff', 
};

const iconMap = {
    AiOutlineTeam,
    AiOutlineCheckCircle,
    AiOutlineWarning,
    AiOutlineCloseCircle,
    AiOutlineSchedule,
    AiOutlineCalendar,
    AiOutlineClockCircle,
    AiOutlineDollar,
    BsPeople: BsPeople,
    BsCheckCircle: BsCheckCircle,
    BsExclamationTriangle: BsExclamationTriangle
};

const PanelComercial = () => {
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false); 
    
    const defaultStartDate = null; 
    const defaultEndDate = null; 
    
    const [startDate, setStartDate] = useState(''); 
    const [endDate, setEndDate] = useState('');

    const controllerRef = useRef(null);
    
    const fetchData = useCallback(async (filters, triggeredByFilter = false) => {
        
        if (triggeredByFilter) {
            setIsFiltering(true);
        } else {
            setLoading(true);
        }
        
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        const { signal } = controllerRef.current;
        
        try {
            const res = await Api.get('/api/comercial/dashboard', { 
                params: { 
                    vencimentoInicio: filters.vencimentoInicio || undefined, 
                    vencimentoFim: filters.vencimentoFim || undefined,
                },
                signal 
            }); 
            
            if (!signal.aborted) {
                setData(res.data);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Erro ao carregar dados do painel comercial:', error);
                setData(null); 
            }
        } finally {
            if (!signal.aborted) {
                setLoading(false);
                setIsFiltering(false); 
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const handleFilter = useCallback(() => {
        if (!startDate || !endDate) { 
            alert("Selecione datas válidas para o filtro."); 
            return; 
        }
        if (new Date(startDate) > new Date(endDate)) { 
            alert("A Data Inicial não pode ser maior que a Data Final."); 
            return; 
        }
        
        fetchData({
            vencimentoInicio: startDate,
            vencimentoFim: endDate,
        }, true);
    }, [startDate, endDate, fetchData]);


    const handleClearFilter = useCallback(() => {
        setStartDate('');
        setEndDate('');
        
        fetchData({
            vencimentoInicio: defaultStartDate,
            vencimentoFim: defaultEndDate,
        }, true);
    }, [fetchData]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchData({
            vencimentoInicio: defaultStartDate,
            vencimentoFim: defaultEndDate,
        });

        return () => {
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
        };
    }, []); 
    
    
    if (loading) return <Loading />; 

    const currentPeriodText = data?.period?.startDate && data?.period?.endDate 
        ? `Dados do período: ${moment(data.period.startDate).format('DD/MM/YYYY')} a ${moment(data.period.endDate).format('DD/MM/YYYY')}`
        : "Dados do período: Todos (Padrão)";

    if (!data || data.error) {
        return (
            <VStack spacing={8} align="stretch" p={6}>
                 <DashboardHeader 
                     title="Painel de Oportunidades"
                     periodText={data?.error ? "Erro ao carregar" : "Dados do período: Todos (Padrão)"}
                     startDate={startDate} 
                     setStartDate={setStartDate} 
                     endDate={endDate} 
                     setEndDate={setEndDate} 
                     onFilterClick={handleFilter}
                     onClearFilter={handleClearFilter}
                     isDisabled={isFiltering}
                 />
                 <Flex justify="center" align="center" h="50vh">
                     <Text color="gray.500">{data?.error?.message || data?.error?.details || data?.error || "Não foi possível carregar os dados."}</Text>
                 </Flex>
            </VStack>
        );
    }

    const { 
        cards: summaryCards = [], 
        charts = {}, 
        grids = {}, 
        insights = [], 
    } = data;
    
    const productDistributionData = charts.productDistribution || [];
    const clientStatusData = charts.clientStatus || []; 
    const rawGridData = grids.summarizedFinanceGrid || grids.crossSellTable || []; 
    const inadimplenciaMensal = charts.inadimplenciaMensal || [];
    const inadimplenciaPorTag = charts.inadimplenciaPorTag || [];
    
    const formattedClientStatusData = (clientStatusData || [])
        .filter(item => 
            item.status === 'Em Dia' || item.status === 'Inadimplente'
        )
        .map(item => ({
            name: item.status, 
            value: item.count,
            color: STATUS_COLORS[item.status] || '#CBD5E0',
            rawCount: Math.round(item.count)
        }));

    
    const tableData = rawGridData.map(row => ({
        franquia: row.franquia,
        marca: row.marca,
        nome_FRANQUIA_MARCA_PROPRIA: row.nome_FRANQUIA_MARCA_PROPRIA,
        descricao_FRANQUIA_MARCA_CUSTOS: row.descricao_FRANQUIA_MARCA_CUSTOS,
        data_vencimento_grid: row.data_vencimento_grid,
        valorNogle: row.valorNogle,
        vencido: row.vencido
    }));
    
    const tableTitle = "Tabela ";

    const BLOCK_PADDING_X = 6; 

    return (
        <VStack 
            spacing={6} 
            align="stretch" 
            w="100%"
            p={0} 
            pt={6} 
        > 
            
            {/* 1. Título e Período (com Filtros) */}
            <Box w="100%" px={BLOCK_PADDING_X}>
                <DashboardHeader 
                    title="Painel de Oportunidades"
                    periodText={currentPeriodText}
                    startDate={startDate} 
                    setStartDate={setStartDate} 
                    endDate={endDate} 
                    setEndDate={setEndDate} 
                    onFilterClick={handleFilter}
                    onClearFilter={handleClearFilter}
                    isDisabled={isFiltering}
                />
            </Box>
            
            <Box w="100%" px={BLOCK_PADDING_X}>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6} mb={8}>
                    {summaryCards.map((card, index) => (
                        <ChakraTooltip 
                            label={card.description || card.title} 
                            bg="gray.700" 
                            color="white" 
                            borderRadius="md"
                            key={card.title + index + '_tooltip'}
                        >
                            <Box
                                bg="surface" 
                                borderRadius="lg"
                                border="1px solid"
                                borderColor="gray.800" 
                                transition="border-color 0.2s"
                                _hover={{ borderColor: 'green.300' }}
                            >
                                <StatusCard 
                                    key={card.title + index}
                                    title={card.title}
                                    value={card.value}
                                    change={card.change}
                                    isPositive={card.change !== undefined ? (parseFloat(String(card.change).replace(/[^\d.,-]/g, '').replace(',', '.')) >= 0) : true}
                                    colorScheme="green"
                                    icon={iconMap[card.icon]} 
                                    format={card.format}
                                />
                            </Box>
                        </ChakraTooltip>
                    ))}
                </SimpleGrid>
            </Box>

            <SimpleGrid 
                columns={{ base: 1, xl: 3 }} 
                spacing={6} 
                minH="400px" 
                w="100%" 
                px={BLOCK_PADDING_X} 
            >
                <Box gridColumn={{ base: 'auto', xl: 'span 2' }} bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                    <Heading as="h3" size="md" color="white" mb={4}>Distribuição de Produtos por Cliente</Heading>
                    <Box h="320px">
                        <ProductDistributionChart 
                            data={productDistributionData} 
                            barColor="green.400" 
                        />
                    </Box>
                </Box>
                
                <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                    <Heading as="h3" size="md" color="white" mb={4}>Status dos Clientes</Heading>
                    <Box h="320px">
                        <ClientStatusChart data={formattedClientStatusData} /> 
                    </Box>
                </Box>
            </SimpleGrid>

            {/* 3. Inadimplência */}
            <Box w="100%" px={BLOCK_PADDING_X}>
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                    <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                        <Heading as="h3" size="md" color="white" mb={1}>Inadimplência — Evolução Mensal</Heading>
                        <Text fontSize="xs" color="gray.500" mb={4}>Contas a receber em aberto e vencidas (últimos 3 meses)</Text>
                        <Box h="280px">
                            <InadimplenciaChart data={inadimplenciaMensal} />
                        </Box>
                    </Box>
                    <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                        <Heading as="h3" size="md" color="white" mb={1}>Inadimplência — Por Tag de Produto</Heading>
                        <Text fontSize="xs" color="gray.500" mb={4}>Inadimplência agrupada por tipo de produto (tag)</Text>
                        <Box h="280px">
                            <InadimplenciaPorTagChart data={inadimplenciaPorTag} />
                        </Box>
                    </Box>
                </SimpleGrid>
            </Box>

            <VStack 
                spacing={6} 
                w="100%" 
                px={BLOCK_PADDING_X}
                align="stretch"
            >
                <SimpleGrid 
                    columns={{ base: 1, xl: 3 }} 
                    spacing={6} 
                    w="100%" 
                >
                    {insights.map((insight, index) => (
                        <InsightCard key={index} text={insight.text} />
                    ))}
                </SimpleGrid>
            </VStack>
            
            <Box 
                bg="surface" 
                p={6} 
                borderRadius="lg" 
                border="1px solid" 
                borderColor="gray.800" 
                mb={8} 
                w="100%" 
                px={BLOCK_PADDING_X} 
            >
                <Heading as="h3" size="md" color="white" mb={4}>{tableTitle}</Heading>
                <CrossSellTable data={tableData} /> 
            </Box>
        </VStack>
    );
};

export default PanelComercial;